import { currencies } from '~/constants/currency';
import { FAILED_ADDRESS_VALIDATION } from '~/constants/errors/apiMessageErrorCodes';
import { format, isSameDay } from 'date-fns';
import flatten from 'lodash-es/flatten';
import forOwn from 'lodash-es/forOwn';
import keyBy from 'lodash-es/keyBy';
import * as moment from 'moment';
import hasRatingErrors from '~/pages/Orders/All/OrderDetailsPage/RatingErrors/hasRatingErrors';
import { SelectedRates } from '~/reducers/rates/rates.reducer';
import { createSelector } from 'reselect';
import {
  getBatchEntriesByOrderId,
  getBatchEntriesIdByOrderObjectId,
  getBatchEntryAssociatedRules,
  getBatchEntryById,
  getBatchEntryData,
  getBatchEntryLoadingStates,
  makeGetBatchEntryById,
  makeGetBatchEntryByIds,
} from '~/selectors/batch-entry.selectors';
import {
  getBatchEntryIdFromProps,
  getBatchEntryIdsFromProps,
  getOrderObjectIdFromProps,
} from '~/selectors/props.selectors';
import { areAddressesFullyEqual, areAllAddressesEqual, BaseAddress } from '~/types/Address';
import APIMessage, { messageIsVisible } from '~/types/APIMessage';
import BusinessRule from '~/types/BusinessRule';
import { IncompleteRegStatus, RegStatusByCarrierAccount } from '~/types/CarrierAccountRegStatus';
import Order, {
  OrderObjectId,
  OrderSelection,
  OrdersRenderData,
  SelectedOrdersAddresses,
} from '~/types/Order';
import {
  BatchEntryExtended,
  getBatchEntriesIdsSortedByRank,
  isReadyForPurchase,
  requiresNewRates,
} from '~/types/OrderBatchEntry';
import { BatchEntryId, BatchEntryStatus } from '~/types/OrdersBatch';
import Rate from '~/types/Rate';
import Shipment from '~/types/Shipment';
import Transaction, { TransactionStatus } from '~/types/Transaction';
import { getCarrierAccountRegStatusesIncomplete } from './carrier-accounts.selectors';
import { getFailedBatchEntriesMessages } from './orders-batch.selectors';
import {
  getIsUpdatingOrder,
  getOrders,
  getOrdersData,
  getSelectedCount,
  getSelectedOrders,
  getTotalOrders,
} from './orders.selectors';
import { getActiveRates, getRatesIdsByShipmentId, getSelectedRates } from './rates.selectors';
import { getShipmentLoadingStates, getShipmentsData } from './shipments.selectors';
import { getTransactionsData } from './transactions.selectors';
import {
  getIsUserAddressValid,
  getUserAddressIfValid,
  getUserReturnAddressIfValid,
} from './user.selectors';

export const makeGetShipmentIsLoadingFromBatchEntries = () => {
  const getBatchEntryByIds = makeGetBatchEntryByIds();

  return createSelector(
    getBatchEntryByIds,
    getShipmentLoadingStates,
    (batchEntries, shipmentLoadingStates): boolean => {
      return (
        batchEntries
          ?.map((b) => {
            if (!b || !b.shipmentObjectId) return false;
            // There's a shipment object id but the fetch wasn't triggered yet
            if (!shipmentLoadingStates.hasOwnProperty(b.shipmentObjectId)) {
              return true;
            }
            return !!shipmentLoadingStates[b.shipmentObjectId];
          })
          .some((b) => b) || false
      );
    },
  );
};

export function batchEntryShipmentIsLoading(
  shipmentLoadingStates: Partial<Record<string, boolean>>,
  batchEntry?: BatchEntryExtended,
): boolean {
  if (!batchEntry || !batchEntry.shipmentObjectId) return false;

  // There's a shipment object id but the fetch wasn't triggered yet
  if (!shipmentLoadingStates.hasOwnProperty(batchEntry.shipmentObjectId)) {
    return true;
  }

  return !!shipmentLoadingStates[batchEntry.shipmentObjectId];
}

export const makeGetShipmentIsLoadingFromBatchEntry = () => {
  const getBatchEntryById = makeGetBatchEntryById();

  return createSelector(
    getBatchEntryById,
    getShipmentLoadingStates,
    (batchEntry, shipmentLoadingStates): boolean => {
      return batchEntryShipmentIsLoading(shipmentLoadingStates, batchEntry || undefined);
    },
  );
};

export const getShipmentIsLoadingFromBatchEntry = createSelector(
  getBatchEntryById,
  getShipmentLoadingStates,
  (batchEntry, shipmentLoadingStates): boolean => {
    if (!batchEntry || !batchEntry.shipmentObjectId) return false;

    // There's a shipment object id but the fetch wasn't triggered yet
    if (!shipmentLoadingStates.hasOwnProperty(batchEntry.shipmentObjectId)) {
      return true;
    }

    return !!shipmentLoadingStates[batchEntry.shipmentObjectId];
  },
);

export const makeGetIsBatchEntryTransactionSuccess = () => {
  const getBatchEntryById = makeGetBatchEntryById();

  return createSelector(
    getBatchEntryById,
    getOrdersData,
    getTransactionsData,
    (batchEntry, orders, transactions): boolean => {
      if (!batchEntry) {
        return false;
      }

      const order = orders[batchEntry.orderObjectId];

      const transactionId = batchEntry.transactionObjectId;
      const transactionFromBatchEntry = transactionId ? transactions[transactionId] : null;
      const transaction = order?.transactions?.find(
        (transaction) => transaction.objectId === transactionId,
      );
      return (transactionFromBatchEntry || transaction)?.status === TransactionStatus.SUCCESS;
    },
  );
};

export const getRatesForBatchEntry = createSelector(
  getBatchEntryById,
  getActiveRates,
  getRatesIdsByShipmentId,
  (batchEntry, activeRates, ratesByShipment) => {
    if (!batchEntry || !batchEntry.shipmentObjectId) return [];
    const rateIds = ratesByShipment[batchEntry.shipmentObjectId] || [];

    return rateIds.map((rateId) => activeRates[rateId]);
  },
);

export const getSelectedRateBatchEntry = createSelector(
  getBatchEntryById,
  getActiveRates,
  getSelectedRates,
  (batchEntry, activeRates, selectedRates): Maybe<Rate> => {
    if (!batchEntry || !batchEntry.shipmentObjectId) return null;

    const selectedRate = selectedRates[batchEntry.shipmentObjectId];

    return selectedRate ? activeRates[selectedRate] : null;
  },
);

export const getSelectedBatchEntries = createSelector(
  getSelectedOrders,
  (selectedOrders): number[] => {
    return flatten(Object.values(selectedOrders));
  },
);

export const getSelectedBatchEntriesByOrderId = createSelector(
  getSelectedOrders,
  getOrderObjectIdFromProps,
  (selectedOrders, orderObjectId): number[] => {
    return selectedOrders[orderObjectId] || [];
  },
);

export const getBatchEntriesDataForSelectedOrders = createSelector(
  getSelectedBatchEntries,
  getBatchEntryData,
  (batchEntryIds, batchEntries): BatchEntryExtended[] =>
    batchEntryIds.map((batchEntryId) => batchEntries[batchEntryId]).filter((v) => v),
);

export const getSelectedBatchEntriesShipments = createSelector(
  getBatchEntriesDataForSelectedOrders,
  getShipmentsData,
  (batchEntries, shipmentData): Shipment[] =>
    batchEntries.reduce((shipments: Shipment[], { shipmentObjectId }) => {
      if (shipmentObjectId) {
        const shipment: Shipment = shipmentData[shipmentObjectId];
        if (shipment) {
          shipments.push(shipment);
        }
      }
      return shipments;
    }, []),
);

const getSelectedBatchEntriesShipmentsMessagesWithoutHidden = createSelector(
  getSelectedBatchEntriesShipments,
  (shipments: Shipment[]): APIMessage[] => {
    const selectedShipmentsMessages = shipments
      .reduce((selectedMessages: APIMessage[], { messages }) => {
        return [...selectedMessages, ...messages];
      }, [])
      .filter(messageIsVisible);
    return selectedShipmentsMessages;
  },
);

// this method uses an object to filter unique messages by carrier, then converts them back to a flat list
export const getUniqueSelectedBatchEntriesShipmentsMessages = createSelector(
  getSelectedBatchEntriesShipmentsMessagesWithoutHidden,
  (messages: APIMessage[]): APIMessage[] => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const messagesByCarrier: Record<string, any> = {};
    messages.forEach((message) => {
      const { text, source } = message;
      const carrierMessages = messagesByCarrier[source as keyof typeof messagesByCarrier];
      if (!carrierMessages) {
        messagesByCarrier[source as keyof typeof messagesByCarrier] = {
          // the code value is not guarenteed to exist, so the only unique
          // data we have to key off of is the actual message. We should move
          // to using the code once they are peroperly enforced and unique
          [text]: message,
        };
      } else {
        carrierMessages[text as keyof typeof carrierMessages] = message;
      }
    });

    const uniqueMessagesArray: APIMessage[] = [];
    forOwn(messagesByCarrier, (messages) => {
      forOwn(messages, (message) => {
        uniqueMessagesArray.push(message);
      });
    });

    return uniqueMessagesArray;
  },
);

// Gets the sender/return addresses of the order selection, prioritizing the batch entries' addresses over the orders' addresses
export const getAddressesFromOrderSelection = createSelector(
  getSelectedOrders,
  getOrdersData,
  getBatchEntryData,
  getUserAddressIfValid,
  getUserReturnAddressIfValid,
  (
    orderSelection,
    ordersData,
    batchEntryData,
    defaultSenderAddress,
    defaultReturnAddress,
  ): SelectedOrdersAddresses => {
    return Object.entries(orderSelection).reduce<{
      senderAddresses: BaseAddress[];
      returnAddresses: BaseAddress[];
    }>(
      (addresses, [orderId, batchEntries]) => {
        const order: Order | undefined = ordersData[orderId]; // This could potentially be undefined

        if (batchEntries.length > 0) {
          // If order selection has batch entries, look at each batch entry's sender/return

          batchEntries.forEach((id) => {
            const batchEntry = batchEntryData[id]; // This could potentially be undefined

            // Gets sender/return from batch entry, fallback on order, then fallback on user defaults
            const senderAddress =
              batchEntry?.addressFrom || order?.addressFrom || defaultSenderAddress;
            const returnAddress =
              batchEntry?.addressReturn || order?.addressReturn || defaultReturnAddress;

            if (
              senderAddress &&
              addresses.senderAddresses.every(
                (address) => !areAddressesFullyEqual(senderAddress, address),
              )
            ) {
              addresses.senderAddresses.push(senderAddress);
            }
            if (
              returnAddress &&
              addresses.returnAddresses.every(
                (address) => !areAddressesFullyEqual(returnAddress, address),
              )
            ) {
              addresses.returnAddresses.push(returnAddress);
            }
          });
        } else {
          // Otherwise if the order selection does not have any batch entries, then look at the order's sender/return

          // Get sender/return from order, fallback on user defaults
          const senderAddress = order?.addressFrom || defaultSenderAddress;
          const returnAddress = order?.addressReturn || defaultReturnAddress;

          if (
            senderAddress &&
            addresses.senderAddresses.every(
              (address) => !areAddressesFullyEqual(senderAddress, address),
            )
          ) {
            addresses.senderAddresses.push(senderAddress);
          }
          if (
            returnAddress &&
            addresses.returnAddresses.every(
              (address) => !areAddressesFullyEqual(returnAddress, address),
            )
          ) {
            addresses.returnAddresses.push(returnAddress);
          }
        }

        return addresses;
      },
      { senderAddresses: [], returnAddresses: [] },
    );
  },
);

export const getAreAllSenderReturnAddressesEqual = createSelector(
  getAddressesFromOrderSelection,
  (addressesFromOrderSelection): boolean => {
    return areAllAddressesEqual(
      addressesFromOrderSelection.senderAddresses.concat(
        addressesFromOrderSelection.returnAddresses,
      ),
    );
  },
);

export const getIsMultipleSenderAddressesSelected = createSelector(
  getAddressesFromOrderSelection,
  (addressesFromOrderSelection): boolean => {
    return addressesFromOrderSelection.senderAddresses.length > 1;
  },
);

export const getIsMultipleReturnAddressesSelected = createSelector(
  getAddressesFromOrderSelection,
  (addressesFromOrderSelection): boolean => {
    return addressesFromOrderSelection.returnAddresses.length > 1;
  },
);

export const getSelectedRatesForSelectedOrders = createSelector(
  getBatchEntriesDataForSelectedOrders,
  getActiveRates,
  getSelectedRates,
  (batchEntries, activeRates, selectedRates): Rate[] => {
    return batchEntries
      .map((batchEntry) => {
        if (!batchEntry.shipmentObjectId) return null;

        const selectedRate = selectedRates[batchEntry.shipmentObjectId];

        return selectedRate ? activeRates[selectedRate] : null;
      })
      .filter((v) => v) as Rate[];
  },
);

export const getOrderObjectIdByBatchEntry = createSelector(
  getBatchEntryData,
  getBatchEntryIdFromProps,
  (batchEntries, batchEntryId): Maybe<string> => {
    return batchEntries[batchEntryId]?.orderObjectId || null;
  },
);

export const getOrderByBatchEntry = createSelector(
  getOrderObjectIdByBatchEntry,
  getOrdersData,
  (orderObjectId, ordersData): Maybe<Order> => (orderObjectId ? ordersData[orderObjectId] : null),
);

// This selector is for selected orders in the new checkout flow
export const getSelectedOrdersTotalCost = createSelector(
  getSelectedRatesForSelectedOrders,

  (rates): { value: number; currencyISO: string } =>
    rates.reduce(
      (obj, rate) => ({
        ...obj,
        value: obj.value + Number(rate.amount),
        currencyISO: rate.currency,
      }),
      { value: 0, currencyISO: currencies.USD.iso },
    ),
);

export const getShipmentByBatchEntry = createSelector(
  getShipmentsData,
  getBatchEntryById,
  (shipmentsData, batchEntry): Maybe<Shipment> =>
    batchEntry && batchEntry.shipmentObjectId
      ? shipmentsData[(batchEntry as BatchEntryExtended).shipmentObjectId!]
      : null,
);

const batchEntriesToBuy = (
  batchEntryIds: number[],
  entriesData: HashMap<number, BatchEntryExtended>,
): BatchEntryExtended[] => {
  return batchEntryIds
    .map((batchEntryId) => entriesData[batchEntryId])
    .filter((v) => v) as BatchEntryExtended[];
};

export const getBatchEntriesToBuy = createSelector(
  getBatchEntryIdsFromProps,
  getBatchEntryData,
  batchEntriesToBuy,
);

export const getBatchEntryIdsFromStore = createSelector(getSelectedOrders, (selectedOrders) =>
  flatten(Object.values(selectedOrders)),
);

export const getBatchEntriesToBuyEntirelyFromStore = createSelector(
  getBatchEntryIdsFromStore,
  getBatchEntryData,
  batchEntriesToBuy,
);

const batchEntriestoBuyMostCommonShipmentDates = (batchEntries: BatchEntryExtended[]): Date => {
  const dateCounts = batchEntries
    .map((batchEntry) => (batchEntry.shipmentDate ? batchEntry.shipmentDate.toDate() : new Date()))
    .reduce<Record<string, number>>((acc, date) => {
      const formattedDate = format(date, 'MM/dd/yy');
      return { ...acc, [formattedDate]: (acc[formattedDate as keyof typeof acc] || 0) + 1 };
    }, {});

  const mostCommon = Object.keys(dateCounts).reduce(
    (a, b) => (dateCounts[a] > dateCounts[b] ? a : b),
    format(new Date(), 'MM/dd/yy'),
  );

  return new Date(mostCommon);
};

export const getBatchEntriesToBuyMostCommonShipmentDate = createSelector(
  getBatchEntriesToBuy,
  batchEntriestoBuyMostCommonShipmentDates,
);

export const getBatchEntriesToBuyMostCommonShipmentDateEntirelyFromStore = createSelector(
  getBatchEntriesToBuyEntirelyFromStore,
  batchEntriestoBuyMostCommonShipmentDates,
);

const validBatchEntriesToBuy = (
  batchEntries: BatchEntryExtended[],
  selectedRates: SelectedRates,
  commonShipmentDate: Date,
): BatchEntryExtended[] => {
  return batchEntries.filter(
    (batchEntry) =>
      isReadyForPurchase(batchEntry) &&
      !!selectedRates[batchEntry.shipmentObjectId!] &&
      isSameDay(
        commonShipmentDate,
        batchEntry.shipmentDate ? batchEntry.shipmentDate!.toDate() : new Date(),
      ),
  );
};

// This works nice for order screen/checkout flow
export const getValidBatchEntriesToBuy = createSelector(
  getBatchEntriesToBuy,
  getSelectedRates,
  getBatchEntriesToBuyMostCommonShipmentDate,
  validBatchEntriesToBuy,
);

// Identical to above but it does not require props
export const getValidBatchEntriesToBuyEntirelyFromStore = createSelector(
  getBatchEntriesToBuyEntirelyFromStore,
  getSelectedRates,
  getBatchEntriesToBuyMostCommonShipmentDateEntirelyFromStore,
  validBatchEntriesToBuy,
);

// object version of getValidBatchEntriesToBuy for faster lookups
export const getValidBatchEntriesToBuyObject = createSelector(
  getValidBatchEntriesToBuyEntirelyFromStore,
  (validBatchEntriesToBuy) => keyBy(validBatchEntriesToBuy, 'id'),
);

// an object keyed by order ID to determine if a partial-parent
// order has at least one purchasable partial-child batchEntry
export const getOrderHasValidBatchEntryToBuyObject = createSelector(
  getValidBatchEntriesToBuyObject,
  getBatchEntriesByOrderId,
  (validBatchEntriesToBuyObject, batchEntriesByOrderId) => {
    const orderHasValidBatchEntryToBuyObject: Record<string, boolean> = {};

    Object.entries(batchEntriesByOrderId).forEach(([orderId, batchEntries]) => {
      // using Array.some to stop looping once we find one valid batch entry to buy
      const hasValidBatchEntryToBuy = (batchEntries as number[]).some(
        (batchEntryId) => validBatchEntriesToBuyObject[batchEntryId],
      );

      if (hasValidBatchEntryToBuy) {
        orderHasValidBatchEntryToBuyObject[
          orderId as keyof typeof orderHasValidBatchEntryToBuyObject
        ] = true;
      }
    });

    return orderHasValidBatchEntryToBuyObject;
  },
);

export const getValidBatchEntriesToBuyCount = createSelector(
  getValidBatchEntriesToBuy,
  (validBatchEntriesToBuy): number => {
    return validBatchEntriesToBuy.length || 0;
  },
);

export const getExcludedBatchEntriesToBuyCount = createSelector(
  getSelectedCount,
  getValidBatchEntriesToBuyCount,
  (selectedCount, validCount): number => selectedCount - validCount,
);

export const getSelectedBatchEntriesValidForCheckout = createSelector(
  getBatchEntriesDataForSelectedOrders,
  getSelectedBatchEntries,
  getSelectedRates,
  (batchEntries, batchEntryIds, selectedRates): BatchEntryExtended[] => {
    return batchEntries.filter(
      (batchEntry) =>
        batchEntryIds.includes(batchEntry.id) &&
        isReadyForPurchase(batchEntry) &&
        !!selectedRates[batchEntry.shipmentObjectId!],
    );
  },
);

export const getSelectedOrdersValidForCheckout = createSelector(
  getBatchEntriesDataForSelectedOrders,
  getOrdersData,
  getSelectedRates,
  (batchEntries, ordersData, selectedRates): Order[] =>
    batchEntries
      .filter(
        (batchEntry) =>
          isReadyForPurchase(batchEntry) && !!selectedRates[batchEntry.shipmentObjectId!],
      )
      .map((batchEntry) => ordersData[batchEntry.orderObjectId])
      .filter((v) => v),
);

export const getIsBatchEntryReadyForCheckout = createSelector(
  getBatchEntryById,
  getSelectedRates,
  getIsUpdatingOrder,
  (batchEntry, selectedRates, isUpdatingOrder): boolean => {
    if (!batchEntry) return false;

    return (
      !!selectedRates[batchEntry.shipmentObjectId!] &&
      isReadyForPurchase(batchEntry) &&
      !isUpdatingOrder
    );
  },
);

export const makeGetDoesBatchEntryNeedNewRates = () => {
  const getBatchEntryById = makeGetBatchEntryById();

  return createSelector(getBatchEntryById, (batchEntry): boolean => {
    if (!batchEntry) return false;

    return (
      batchEntry.objectStatus === BatchEntryStatus.PENDING &&
      batchEntry.shipmentObjectId === null &&
      !!batchEntry.parcel
    );
  });
};

export const getRatesForBatchEntriesToBuy = createSelector(
  getBatchEntriesToBuy,
  getSelectedRates,
  getActiveRates,
  getBatchEntriesToBuyMostCommonShipmentDate,
  (batchEntries, selectedRates, activeRates, commonShipmentDate): Rate[] =>
    batchEntries
      .map((batchEntry) => {
        if (
          !isSameDay(
            commonShipmentDate,
            batchEntry.shipmentDate ? batchEntry.shipmentDate!.toDate() : new Date(),
          )
        ) {
          return null;
        }
        if (!batchEntry.shipmentObjectId || batchEntry.transactionObjectId) return null;
        const rateId = selectedRates[batchEntry.shipmentObjectId];

        return activeRates[rateId];
      })
      .filter((v): v is Rate => !!v),
);

export const getReturnsCountForBatchEntriesToBuy = createSelector(
  getBatchEntriesToBuy,
  (batchEntries): number =>
    batchEntries.reduce((count, batchEntry) => {
      if (batchEntry.extras && batchEntry.extras.isReturnRequested) {
        return count + 1;
      }

      return count;
    }, 0),
);

export const makeGetBatchEntryTransactionFromOrder = () => {
  const getBatchEntryById = makeGetBatchEntryById();

  return createSelector(
    getBatchEntryById,
    getOrdersData,
    (batchEntry, ordersData): Maybe<Transaction> => {
      if (!batchEntry) return null;
      const order = ordersData[batchEntry.orderObjectId];

      return (
        (order &&
          batchEntry &&
          order.transactions &&
          order.transactions.find(
            (transaction) => transaction.objectId === batchEntry.transactionObjectId,
          )) ||
        null
      );
    },
  );
};

export const getIsBatchEntryTransactionSuccess = createSelector(
  getBatchEntryIdFromProps,
  getBatchEntryData,
  getOrdersData,
  getTransactionsData,
  (batchEntryId, batchEntries, orders, transactions): boolean => {
    const batchEntry = batchEntries[batchEntryId];
    if (!batchEntry) {
      return false;
    }

    const order = orders[batchEntry.orderObjectId];

    const transactionId = batchEntry.transactionObjectId;
    const transactionFromBatchEntry = transactionId ? transactions[transactionId] : null;
    const transaction = order?.transactions?.find(
      (transaction) => transaction.objectId === transactionId,
    );
    return (transactionFromBatchEntry || transaction)?.status === TransactionStatus.SUCCESS;
  },
);

export const getSelectedOrdersMostCommonShipmentDate = createSelector(
  getBatchEntriesDataForSelectedOrders,
  (batchEntries): Date => {
    const dateCounts = batchEntries
      .map((batchEntry) =>
        batchEntry.shipmentDate ? batchEntry.shipmentDate!.toDate() : new Date(),
      )
      .reduce<Record<string, number>>((acc, date) => {
        const formattedDate = format(date, 'MM/dd/yy');
        return { ...acc, [formattedDate]: (acc[formattedDate] || 0) + 1 };
      }, {});

    const mostCommon = Object.keys(dateCounts).reduce(
      (a, b) => (dateCounts[a] > dateCounts[b] ? a : b),
      format(new Date(), 'MM/dd/yy'),
    );

    return new Date(mostCommon);
  },
);

export const getRuleForBatchEntry = createSelector(
  getBatchEntryAssociatedRules,
  getBatchEntryIdFromProps,
  (rules, batchEntryId): Maybe<BusinessRule[]> => {
    return (rules && rules[batchEntryId]) || null;
  },
);

export const getOrdersTableData = createSelector(
  getOrders,
  getBatchEntryData,
  getBatchEntriesByOrderId,
  (orders, batchEntriesData, batchEntriesByOrderId): OrdersRenderData[] =>
    orders.reduce((data: OrdersRenderData[], order: Order) => {
      const bes = batchEntriesByOrderId[order.objectId] || [];

      // Single order, might or might not have a batchentry associated
      if (bes.length <= 1) {
        return [
          ...data,
          {
            order,
            kind: 'single',
            batchEntry: bes[0] ? batchEntriesData[bes[0]] : null,
          } as OrdersRenderData,
        ];
      }

      const batchEntriesSortedByRank = getBatchEntriesIdsSortedByRank(bes, batchEntriesData);

      // Split order. This for sure will have batch entries.
      return [
        ...data,
        { order, kind: 'partial-parent' } as OrdersRenderData,
        ...batchEntriesSortedByRank.map(
          (beId) =>
            ({
              order,
              kind: 'partial-child',
              batchEntry: batchEntriesData[beId],
            }) as OrdersRenderData,
        ),
      ];
    }, []),
);

export const getTotalOrdersTableData = createSelector(getTotalOrders, (orders) => orders);

export const getAreAllPartialFulfillmentRequestsSelected = createSelector(
  getSelectedBatchEntriesByOrderId,
  getBatchEntriesIdByOrderObjectId,
  (selectedBatchEntries, batchEntriesByOrder): boolean =>
    selectedBatchEntries.length === batchEntriesByOrder.length,
);

// This one might not be needed
export const getAreSomePartialFulfillmentRequestsSelected = createSelector(
  getSelectedBatchEntriesByOrderId,
  getBatchEntriesIdByOrderObjectId,
  (selectedBatchEntries, batchEntriesByOrder): boolean =>
    !!selectedBatchEntries.length && selectedBatchEntries.length < batchEntriesByOrder.length,
);

export const getAreSelectedBatchEntriesLoading = createSelector(
  getSelectedBatchEntries,
  getBatchEntryData,
  getBatchEntryLoadingStates,
  getShipmentLoadingStates,
  (
    selectedBatchEntries,
    batchEntryData,
    batchEntryLoadingStates,
    shipmentLoadingStates,
  ): boolean => {
    return selectedBatchEntries.some((batchEntryId) => {
      const batchEntry = batchEntryData[batchEntryId];

      if (!batchEntry) return false;
      if (batchEntryLoadingStates[batchEntry.orderObjectId]) return true;
      // BatchEntry has reached end state
      if (
        [
          BatchEntryStatus.COMPLETE_TRANSACTION,
          BatchEntryStatus.TRANSACTION_REQUEST_FAILED,
        ].includes(batchEntry.objectStatus)
      ) {
        return false;
      }

      if (!batchEntry.shipmentObjectId) {
        return false;
      }

      if (batchEntry.objectStatus === BatchEntryStatus.PROCESSING_SHIPMENT) return true;

      // There's a shipment object id but the fetch wasn't triggered yet
      if (!shipmentLoadingStates.hasOwnProperty(batchEntry.shipmentObjectId)) return true;

      return !!shipmentLoadingStates[batchEntry.shipmentObjectId];
    });
  },
);

export const getIsAnyBatchEntryASplitOrder = createSelector(
  getBatchEntryIdsFromProps,
  getBatchEntryData,
  (batchEntryIds, batchEntryData): boolean => {
    return batchEntryIds.some((batchEntryId) => {
      if (!batchEntryId || !batchEntryData || !batchEntryData[batchEntryId]) return false;

      const { partialFulfillmentRank } = batchEntryData[batchEntryId];

      return !!partialFulfillmentRank && partialFulfillmentRank > 0;
    });
  },
);

// Iterates through rates to buy to find if a rate is from an account that has an incomplete reg status
// Returns a mapping of carrier account object id to incomplete reg status for any found
export const getIncompleteRegStatusFromRates = createSelector(
  getRatesForBatchEntriesToBuy,
  getCarrierAccountRegStatusesIncomplete,
  (
    ratesToBuy: Rate[],
    regStatuses: { [carrierAccount: string]: IncompleteRegStatus },
  ): RegStatusByCarrierAccount => {
    const regStatusByCarrierAccount = ratesToBuy.reduce((acc, rate) => {
      const carrierAccountObjectId = rate.carrierAccount;
      const regStatus = regStatuses[carrierAccountObjectId];

      if (regStatus) {
        return { ...acc, [carrierAccountObjectId]: regStatus };
      } else {
        return acc;
      }
    }, {});

    return regStatusByCarrierAccount;
  },
);

export const getAllOrders = createSelector(
  getOrders,
  getBatchEntriesByOrderId,
  (orders, beByOrder): OrderSelection => {
    return orders.reduce((obj, curr) => {
      return {
        ...obj,
        [curr.objectId]: beByOrder[curr.objectId] || [],
      };
    }, {});
  },
);

// This is a selector to make the transition from order being the primary focus, to batch entry.
// Should be deprecated after that work is finished
export const getFirstBatchEntryIdFromOrder = createSelector(
  getBatchEntriesByOrderId,
  getOrderObjectIdFromProps,
  (batchEntriesByOrder, orderObjectId): Maybe<number> => {
    const batchEntries = batchEntriesByOrder[orderObjectId] || [];
    if (batchEntries.length !== 1) return null;

    return batchEntries[0];
  },
);

// This is a selector to make the transition from order being the primary focus, to batch entry.
// Should be deprecated after that work is finished
export const getFirstBatchEntryFromOrder = createSelector(
  getFirstBatchEntryIdFromOrder,
  getBatchEntryData,
  (batchEntryId, batchEntryData): Maybe<BatchEntryExtended> => {
    if (!batchEntryId) return null;

    return batchEntryData[batchEntryId];
  },
);

export const getFailedAddressValidationMessages = createSelector(
  getFailedBatchEntriesMessages,

  (apiMessagesByOrderId) =>
    Object.entries(apiMessagesByOrderId)
      .filter(
        ([, apiMessages]) =>
          !!apiMessages.filter((apiMessage) => apiMessage.code === FAILED_ADDRESS_VALIDATION)
            .length,
      )
      .map(([key, apiMessages]) => ({
        orderId: key,
        errorMessages: apiMessages,
      })),
);

export const getUnpurchasedBatchEntriesOrderSelectionWithExpiredShipmentDate = createSelector(
  getBatchEntryData,
  getShipmentsData,
  getOrdersData,
  getIsUserAddressValid,
  (
    batchEntryData,
    shipmentsData,
    ordersData,
    isUserSenderAddressValid,
  ): Record<OrderObjectId, BatchEntryId[]> => {
    const unpurchasedBatchEntriesWithExpiredShipmentDate: Record<OrderObjectId, BatchEntryId[]> =
      {};
    Object.values(batchEntryData).forEach((batchEntry) => {
      if (
        [BatchEntryStatus.COMPLETE_SHIPMENT, BatchEntryStatus.PENDING].includes(
          batchEntry.objectStatus,
        )
      ) {
        let isUnpurchasedBatchEntryWithExpiredShipmentDate = false;

        // Checks batch entry shipment date
        if (batchEntry.shipmentDate?.isBefore(moment(), 'day')) {
          isUnpurchasedBatchEntryWithExpiredShipmentDate = true;
        }
        // when batchEntry.shipmentDate is null, we could check shipment.submissionDate
        else if (
          batchEntry.shipmentObjectId &&
          shipmentsData[batchEntry.shipmentObjectId]?.shipmentDate.isBefore(moment(), 'day')
        ) {
          isUnpurchasedBatchEntryWithExpiredShipmentDate = true;
        }
        // batch entry was invalid for checkout so the shipment might be nulled out (done in Armor)
        else if (
          batchEntry.objectStatus === BatchEntryStatus.PENDING &&
          batchEntry.parcel &&
          ordersData[batchEntry.orderObjectId] &&
          !hasRatingErrors(
            ordersData[batchEntry.orderObjectId],
            batchEntry,
            null,
            null,
            [],
            isUserSenderAddressValid,
          ).hasErrors
        ) {
          isUnpurchasedBatchEntryWithExpiredShipmentDate = true;
        }

        // aggregate the entry into the returned record
        if (isUnpurchasedBatchEntryWithExpiredShipmentDate) {
          if (unpurchasedBatchEntriesWithExpiredShipmentDate[batchEntry.orderObjectId]) {
            unpurchasedBatchEntriesWithExpiredShipmentDate[batchEntry.orderObjectId].push(
              batchEntry.id,
            );
          } else {
            unpurchasedBatchEntriesWithExpiredShipmentDate[batchEntry.orderObjectId] = [
              batchEntry.id,
            ];
          }
        }
      }
    });
    return unpurchasedBatchEntriesWithExpiredShipmentDate;
  },
);

export const getOrderEntriesRequiringNewRates = createSelector(
  getBatchEntryData,
  (batchEntryData): OrderSelection => {
    return Object.values(batchEntryData).reduce<Record<string, number[]>>(
      (selection, batchEntry) => {
        if (requiresNewRates(batchEntry)) {
          if (selection[batchEntry.orderObjectId]) {
            selection[batchEntry.orderObjectId].push(batchEntry.id);
          } else {
            selection[batchEntry.orderObjectId] = [batchEntry.id];
          }
        }
        return selection;
      },
      {},
    );
  },
);
